import { Flex, SimpleGrid } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCompanyOptions } from "../../api/hooks/customer/useCompanyOptions.ts";
import { useLimitRequests } from "../../api/hooks/limitRequest/useLimitRequests.ts";
import Layout from "../../components/Layout/Layout";
import { Select } from "../../components/Select.tsx";
import Table from "../../components/Table/Table.tsx";
import { SortDirection, TableRow } from "../../components/Table/types.ts";
import Title from "../../components/Title/Title";
import { AppRoute } from "../../constants.ts";
import {
  LimitRequestFilter,
  LimitRequestOrderBy,
  OrderBy,
} from "../../graphql/generated.ts";
import { LimitRequestFiltersValue } from "../../types/filter.ts";
import {
  LimitRequest,
  limitRequestStatusOptions,
  limitRequestTypeOptions,
} from "../../types/limitRequest/limitRequest.ts";
import { getCompanyOptions } from "../../utils/company.ts";
import { getLimitRequestApiFilter } from "../../utils/filter.ts";
import { getPagesCount } from "../../utils/pagination.ts";
import { getLimitRequestApiSort } from "../CreditUnderwriting/utils.ts";
import { useSubmissionListTableData } from "./hooks/useSubmissionListTableData.tsx";

const SubmissionListPage = () => {
  const [{ data: companiesData, fetching: fetchingCompanies }] =
    useCompanyOptions();
  const companies = companiesData?.customers.data;

  const navigate = useNavigate();

  const [filter, setFilter] = useState<LimitRequestFilter>({});
  const [orderBy, setOrderBy] = useState<LimitRequestOrderBy>({
    createdAt: OrderBy.Desc,
  });
  const [page, setPage] = useState<number>(1);

  const [{ data: response, fetching, error }] = useLimitRequests({
    filter: filter,
    orderBy,
    page,
  });
  const data: LimitRequest[] | undefined | null = response?.limitRequests?.data;
  const pageCount = getPagesCount(response?.limitRequests?.total);

  const handleSortChange = useCallback(
    (sort: DataTableSortStatus) => {
      setOrderBy(getLimitRequestApiSort(sort));
    },
    [setOrderBy]
  );

  const handleRowClik = useCallback(
    (record: TableRow) => {
      navigate(`${AppRoute.LimitRequest}/${record.id}`);
    },
    [navigate]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handleFilterChange = useCallback(
    (key: keyof LimitRequestFiltersValue, value: unknown) => {
      setFilter(getLimitRequestApiFilter({ [key]: value }));
    },
    []
  );

  const [columns, rows] = useSubmissionListTableData({ data });

  if (error) {
    notifications.clean();
    notifications.show({
      message: "Something went wrong while trying to fetch data.",
      variant: "error",
    });
  }

  return (
    <Layout>
      <Flex direction="column" gap={{ base: 16, sm: 24 }}>
        <Title>Submission List</Title>
        <SimpleGrid
          cols={5}
          breakpoints={[{ maxWidth: "sm", cols: 1 }]}
          spacing={20}
          verticalSpacing={8}
        >
          <Select
            loading={fetchingCompanies}
            size="m"
            clearable
            searchable
            label="Company Name"
            placeholder="Company Name"
            data={getCompanyOptions(companies)}
            onChange={(value) => handleFilterChange("companyName", value)}
          />
          <Select
            size="m"
            clearable
            label="Limit Request Type"
            placeholder="Limit Request Type"
            data={limitRequestTypeOptions}
            onChange={(value) => handleFilterChange("limitRequestType", value)}
          />
          <Select
            size="m"
            clearable
            label="Current Process"
            placeholder="Current Process"
            data={limitRequestStatusOptions}
            onChange={(value) => handleFilterChange("requestStatus", value)}
          />
        </SimpleGrid>
        <Table
          columns={columns}
          rows={rows}
          defaultSort={{
            columnAccessor: "createdAt",
            direction: SortDirection.asc,
          }}
          loading={fetching}
          pagination={{
            pageCount: pageCount,
            page: page,
            onPageChange: handlePageChange,
          }}
          onRowClick={handleRowClik}
          onSortChange={handleSortChange}
        />
      </Flex>
    </Layout>
  );
};

export default SubmissionListPage;
